import {
  isDuplicateEmail,
  isValidEmail,
  isValidProfessionalEmail,
} from '../../utils/validation';
import { ERROR_TYPES, VALIDATION_MESSAGES } from './constants';

/**
 * @typedef {import('./types').RosterProvider} RosterProvider
 * @typedef {import('../../types/provider').Provider} Provider
 */

/**
 * Check if provider is eligible per API flag.
 * Eligible means they can be added to roster.
 *
 * @param {Provider|null} provider
 * @returns {boolean}
 *
 * @example
 * isProviderEligible({ eligible: true }) // true
 * isProviderEligible({ eligible: false }) // false
 * isProviderEligible(null) // false
 */
function isProviderEligible(provider) {
  return provider?.eligible === true;
}

/**
 * Check if provider is NOT already in the roster.
 *
 * This function checks against both:
 * 1. Providers in the current checkout session (existingProviders from session state)
 * 2. Providers already in the user's roster (existingProviders from backend API)
 *
 * The ProviderSearch component merges both lists before calling this function.
 *
 * @param {Provider|null} provider - The provider to check
 * @param {Array<{providerCode?: string}>} existingProviders - Combined list of:
 *   - Providers in current session (from rosterProviders state)
 *   - Providers in existing roster (from backend API)
 * @returns {boolean} - true if provider is unique (not found), false if duplicate
 *
 * @example
 * isProviderUnique({ providerCode: 'ABC' }, [{ providerCode: 'XYZ' }]) // true
 * isProviderUnique({ providerCode: 'ABC' }, [{ providerCode: 'ABC' }]) // false
 */
function isProviderUnique(provider, existingProviders = []) {
  if (!provider?.providerCode) return false;
  return !existingProviders.some(
    (p) => p.providerCode === provider.providerCode,
  );
}

/**
 * Human-readable messages for provider blocking issues.
 * @type {Record<'ineligible' | 'duplicate', string>}
 */
const BLOCKER_MESSAGES = Object.freeze({
  ineligible:
    'This healthcare professional already has an upgraded profile and is being sponsored by another entity.',
  duplicate:
    'This healthcare professional has already been added to your roster. Please select a different provider to continue.',
});

/**
 * Get the first blocking message for a provider, or null if addable.
 * This is the primary API - returns what to show the user.
 *
 * Priority: ineligible > duplicate (ineligible is checked first)
 *
 * @param {Provider|null} provider
 * @param {Array<{providerCode?: string}>} existingProviders
 * @returns {string|null}
 */
export function getProviderBlockerMessage(provider, existingProviders = []) {
  if (!provider) return null;

  if (!isProviderEligible(provider)) {
    return BLOCKER_MESSAGES.ineligible;
  }

  if (!isProviderUnique(provider, existingProviders)) {
    return BLOCKER_MESSAGES.duplicate;
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// RosterProvider validation (for final "can add" check)
// ─────────────────────────────────────────────────────────────────────────────

/** @param {RosterProvider} p */
function isEmailAccepted(p) {
  return p?.validEmail === true; // passed format + uniqueness check
}

/** @param {RosterProvider} p */
function isIdentityVerified(p) {
  return p?.validLast4 === true; // last-4 matched via API
}

/** @param {RosterProvider} p */
function isEligibleForSubscription(p) {
  return p?.eligible === true; // not already sponsored
}

/**
 * Checks if a provider can complete subscription.
 * All criteria must be met: email valid, last4 verified, eligible.
 *
 * @param {RosterProvider} provider
 * @returns {boolean}
 */
export function canSubscribe(provider) {
  return (
    isEmailAccepted(provider) &&
    isIdentityVerified(provider) &&
    isEligibleForSubscription(provider)
  );
}

/**
 * Providers whose email was rejected (invalid format or already in use)
 * @param {RosterProvider[]} providers
 */
export function filterProvidersWithInvalidEmail(providers) {
  if (!Array.isArray(providers)) {
    return [];
  }

  return providers.filter((p) => !isEmailAccepted(p));
}

// #region Email validation

/**
 * @typedef {Object} ValidationError
 * @property {string} type - Error type (e.g., 'invalidInput', 'alreadyExists')
 * @property {string} message - User-facing error message
 */

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Whether the email is valid
 * @property {ValidationError|null} error - Error details if invalid, null if valid
 */

/**
 * @typedef {Object} ValidateEmailOptions
 * @property {Array<{email: string}>} [otherProviders] - Other providers to check for duplicates
 * @property {boolean} [skipDuplicateCheck] - Skip duplicate validation (useful for untouched inputs)
 * @property {boolean} [skipProfessionalCheck] - Skip professional-domain check (e.g. when email is autopopulated from provider/membership)
 * @property {Object} ERROR_TYPES - Error type constants
 * @property {Object} VALIDATION_MESSAGES - Validation message constants
 */

/**
 *
 * @param {string} email
 * @param {Array<{email: string}>} [otherProviders=[]]
 * @param {{ skipProfessionalCheck?: boolean }} [options={}]
 * @returns {ValidationResult}
 */
export const validateRosterProviderEmail = (
  email,
  otherProviders = [],
  options = {},
) => {
  const { skipProfessionalCheck = false } = options;

  if (!email) {
    return {
      isValid: false,
      error: {
        type: ERROR_TYPES.INVALID_INPUT,
        message: VALIDATION_MESSAGES.EMAIL_REQUIRED,
      },
    };
  }

  if (!isValidEmail(email)) {
    return {
      isValid: false,
      error: {
        type: ERROR_TYPES.INVALID_INPUT,
        message: VALIDATION_MESSAGES.EMAIL_INVALID,
      },
    };
  }

  if (!skipProfessionalCheck && !isValidProfessionalEmail(email)) {
    return {
      isValid: false,
      error: {
        type: ERROR_TYPES.INVALID_INPUT,
        message: VALIDATION_MESSAGES.EMAIL_NOT_PROFESSIONAL,
      },
    };
  }

  if (
    Array.isArray(otherProviders) &&
    otherProviders.length > 0 &&
    isDuplicateEmail(email, otherProviders)
  ) {
    return {
      isValid: false,
      error: {
        type: ERROR_TYPES.ALREADY_EXISTS,
        message: VALIDATION_MESSAGES.DUPLICATE_EMAIL,
      },
    };
  }

  return { isValid: true, error: null };
};

// #endregion Email validation
